/**
 * Sanitized Publishing API — tenant sanitize policy, dry-run tester, and
 * per-repo scan reports (redactions_api backend;
 * specs/changes/docs-sanitization).
 */

import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export type SanitizeAction = 'redact' | 'report' | 'off';
export type SanitizeCategory = 'secret' | 'pii' | 'infra' | 'term';

export interface SanitizePolicy {
  enabled: boolean;
  categories: Record<SanitizeCategory, SanitizeAction>;
  ruleOverrides: Record<string, SanitizeAction>;
  customTerms: string[];
  internalDomains: string[];
  allowlist: string[];
  version: number;
}

export interface SanitizeRuleInfo {
  ruleId: string;
  category: SanitizeCategory;
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  defaultAction: 'redact' | 'report';
}

/** Findings never carry matched plaintext — previews are masked server-side. */
export interface SanitizeFinding {
  ruleId: string;
  category: SanitizeCategory;
  severity: string;
  action: 'redact' | 'report';
  line: number;
  preview: string;
  rid?: string;
}

export interface SanitizeCounts {
  redacted: number;
  reported: number;
  suppressed: number;
}

export interface SanitizeTestResult {
  redactedPreview: string;
  findings: SanitizeFinding[];
  counts: SanitizeCounts;
  truncated: boolean;
}

export interface ScanReport {
  jobId: string;
  scannedAt: string;
  files: number;
  totals: SanitizeCounts;
  byCategory: Record<string, number>;
  policyVersion: number;
  truncated: boolean;
  perFile: { path: string; findings: SanitizeFinding[] }[];
}

/** Payload shape for PUT /docs/sanitize-policy (version is server-owned). */
export type SanitizePolicyInput = Omit<SanitizePolicy, 'version'>;

export const DEFAULT_POLICY: SanitizePolicyInput = {
  enabled: false,
  categories: { secret: 'redact', pii: 'redact', infra: 'redact', term: 'redact' },
  ruleOverrides: {},
  customTerms: [],
  internalDomains: [],
  allowlist: [],
};

/** One entry per non-empty line, trimmed — the editors are line-oriented. */
export function linesToList(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

export function listToLines(items: string[] | undefined): string {
  return (items || []).join('\n');
}

export const sanitizeApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  async getPolicy(): Promise<{ policy: SanitizePolicy; catalog: SanitizeRuleInfo[] }> {
    const { data } = await api.get('/docs/sanitize-policy');
    return data;
  },

  async savePolicy(policy: SanitizePolicyInput): Promise<SanitizePolicy> {
    const { data } = await api.put('/docs/sanitize-policy', policy);
    return data.policy;
  },

  async testPolicy(content: string, policy?: SanitizePolicyInput): Promise<SanitizeTestResult> {
    const { data } = await api.post('/docs/sanitize-policy/test', { content, policy });
    return data;
  },

  async getScanReport(owner: string, repo: string): Promise<ScanReport> {
    const { data } = await api.get(`/repos/${owner}/${repo}/scan-report`);
    return data.report;
  },
};
